import { useEffect, useMemo, useState } from 'react';
import { useLocale } from '../../lib/locale';
import {
  Camera,
  FileText,
  MessageSquare,
  ShoppingBag,
  Info,
  IndianRupee,
  Tag,
  Star,
  Clock,
  MapPin,
  Phone,
  Mail,
  PlayCircle,
  X,
} from 'lucide-react';
import type { Coupon, Restaurant } from '../../App';
import { api } from '../../lib/api';

type TabId = 'overview' | 'order-online' | 'offers' | 'reviews' | 'photos' | 'menu';

interface RestaurantTabsProps {
  restaurant: Restaurant;
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  offers?: Coupon[];
  selectedOfferId?: string | null;
  onOfferSelect?: (offer: Coupon) => void;
  children?: React.ReactNode;
}

interface DynamicRestaurantData {
  id?: string;
  name?: string;
  description?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
  rating?: number | string | null;
  totalReviews?: number | string | null;
  deliveryTime?: string | null;
  deliveryFee?: number | string | null;
  openTime?: string | null;
  closeTime?: string | null;
  amenities?: string[];
  amenityDetails?: Array<{ id?: string; code?: string; name?: string }>;
  cuisine?: string[];
  cuisineDetails?: Array<{ id?: string; code?: string; name?: string }>;
  photos?: Array<{ id?: string; mediaUrl?: string; media_url?: string }>;
  videos?: Array<{ id?: string; mediaUrl?: string; media_url?: string; thumbnailUrl?: string; thumbnail_url?: string }>;
  mediaItems?: Array<{
    id?: string;
    mediaType?: string;
    media_url?: string;
    mediaUrl?: string;
    thumbnailUrl?: string | null;
    thumbnail_url?: string | null;
  }>;
}

interface DynamicMenuCategory {
  id: string;
  name: string;
}

interface DynamicMenuItem {
  id: string;
  name: string;
  price: number;
  isVeg: boolean;
  categoryId?: string;
  categoryName?: string;
  servingSize?: string | null;
}

interface DynamicReview {
  id: string;
  name: string;
  avatar: string;
  rating: number;
  dateLabel: string;
  comment: string;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const toNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const isUuidLike = (value: string): boolean => UUID_REGEX.test(value.trim());

type GalleryMediaType = 'PHOTO' | 'VIDEO';

const inferMediaTypeFromUrl = (url: string): GalleryMediaType => {
  const normalized = String(url || '').trim().toLowerCase();
  if (!normalized) return 'PHOTO';
  if (normalized.includes('/videos/')) return 'VIDEO';
  const withoutQuery = normalized.split('?')[0].split('#')[0];
  const ext = withoutQuery.includes('.') ? withoutQuery.split('.').pop() : '';
  if (!ext) return 'PHOTO';
  if (['mp4', 'webm', 'ogg', 'mov', 'm4v', '3gp'].includes(ext)) return 'VIDEO';
  return 'PHOTO';
};

const parseStringList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item ?? '').trim())
      .filter(Boolean);
  }
  return String(value ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const formatTimeLabel = (value: string | null | undefined): string => {
  if (!value) return 'N/A';
  const raw = String(value).trim();
  const matched = raw.match(/(\d{2}):(\d{2})(?::\d{2})?/);
  const normalized = matched ? `${matched[1]}:${matched[2]}` : raw;
  const [hoursRaw, minutesRaw] = normalized.split(':');
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return normalized;
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${String(hour12).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
};

const buildAddressLabel = (source: DynamicRestaurantData, fallback: Restaurant): string => {
  const parts = [source.address, source.city, source.state, source.country]
    .map((item) => String(item || '').trim())
    .filter(Boolean);
  if (parts.length) return parts.join(', ');
  return fallback.address || 'Address not available';
};

const formatServingSizeLabel = (value: unknown): string | null => {
  const raw = String(value ?? '').trim();
  if (!raw) return null;

  const lower = raw.toLowerCase();
  if (lower.includes('serve')) return raw;

  const rangeMatch = raw.match(/^(\d+)\s*-\s*(\d+)$/);
  if (rangeMatch) return `Serves ${rangeMatch[1]}-${rangeMatch[2]}`;

  if (/^\d+$/.test(raw)) return `Serves ${raw}`;
  return raw;
};

const normalizeMenuItem = (raw: any): DynamicMenuItem | null => {
  const id = String(raw?.id || '').trim();
  const name = String(raw?.name || '').trim();
  if (!id || !name) return null;

  const rawServing = raw?.servingSize ?? raw?.serving_size;
  const servingSize = formatServingSizeLabel(rawServing);

  return {
    id,
    name,
    price: toNumber(raw?.discountPrice ?? raw?.discount_price ?? raw?.price, 0),
    isVeg: Boolean(raw?.isVeg ?? raw?.is_veg ?? true),
    categoryId: String(raw?.categoryId || raw?.category_id || raw?.category?.id || '').trim() || undefined,
    categoryName: String(raw?.category?.name || raw?.category_name || '').trim() || undefined,
    servingSize,
  };
};

const formatReviewDate = (value: unknown): string => {
  const raw = String(value ?? '').trim();
  if (!raw) return 'Recently';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;

  const now = Date.now();
  const diffMs = now - parsed.getTime();
  const diffDays = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 30) return `${diffDays} days ago`;
  return parsed.toLocaleDateString();
};

const normalizeReview = (raw: any): DynamicReview | null => {
  const id = String(raw?.id || '').trim();
  if (!id) return null;
  const name = String(raw?.customer_name || raw?.customerName || raw?.userName || 'Customer').trim();
  const avatar = String(raw?.customer_avatar || raw?.customerAvatar || '').trim();
  const comment = String(raw?.comment || '').trim();
  const rating = toNumber(raw?.rating, 0);
  return {
    id,
    name: name || 'Customer',
    avatar,
    rating: Math.max(0, Math.min(5, rating)),
    dateLabel: formatReviewDate(raw?.created_at ?? raw?.createdAt ?? raw?.date),
    comment: comment || 'No comment provided.',
  };
};

export function RestaurantTabs({
  restaurant,
  activeTab,
  onTabChange,
  offers,
  selectedOfferId,
  onOfferSelect,
  children,
}: RestaurantTabsProps) {
  const { formatMoney } = useLocale();
  const [details, setDetails] = useState<DynamicRestaurantData | null>(null);
  const [menuCategories, setMenuCategories] = useState<DynamicMenuCategory[]>([]);
  const [menuItems, setMenuItems] = useState<DynamicMenuItem[]>([]);
  const [reviews, setReviews] = useState<DynamicReview[]>([]);
  const [cuisineNameMap, setCuisineNameMap] = useState<Record<string, string>>({});
  const [isLoadingDynamicData, setIsLoadingDynamicData] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadDynamicData = async () => {
      if (!restaurant?.id) return;
      if (!isUuidLike(restaurant.id)) {
        setDetails(null);
        setMenuCategories([]);
        setMenuItems([]);
        setReviews([]);
        setCuisineNameMap({});
        return;
      }
      setIsLoadingDynamicData(true);
      try {
        const [restaurantRes, categoriesRes, itemsRes, reviewsRes, cuisineTypesRes] = await Promise.allSettled([
          api.restaurants.getById(restaurant.id),
          api.menu.getCategories(restaurant.id),
          api.menu.getItems(restaurant.id),
          api.reviews.getByRestaurant(restaurant.id),
          api.masterData.cuisineTypes.getAll(),
        ]);

        if (!mounted) return;

        if (restaurantRes.status === 'fulfilled') {
          const data = (restaurantRes.value as any)?.data || {};
          setDetails(data);
        } else {
          setDetails(null);
        }

        if (categoriesRes.status === 'fulfilled') {
          const raw = Array.isArray((categoriesRes.value as any)?.data) ? (categoriesRes.value as any).data : [];
          setMenuCategories(
            raw
              .map((item: any) => ({
                id: String(item?.id || '').trim(),
                name: String(item?.name || item?.food_category?.name || '').trim(),
              }))
              .filter((item: DynamicMenuCategory) => item.id && item.name)
          );
        } else {
          setMenuCategories([]);
        }

        if (itemsRes.status === 'fulfilled') {
          const raw = Array.isArray((itemsRes.value as any)?.data) ? (itemsRes.value as any).data : [];
          setMenuItems(raw.map(normalizeMenuItem).filter(Boolean) as DynamicMenuItem[]);
        } else {
          setMenuItems([]);
        }

        if (reviewsRes.status === 'fulfilled') {
          const raw = Array.isArray((reviewsRes.value as any)?.data) ? (reviewsRes.value as any).data : [];
          setReviews(raw.map(normalizeReview).filter(Boolean) as DynamicReview[]);
        } else {
          setReviews([]);
        }

        if (cuisineTypesRes.status === 'fulfilled') {
          const raw = Array.isArray((cuisineTypesRes.value as any)?.data) ? (cuisineTypesRes.value as any).data : [];
          setCuisineNameMap(
            raw.reduce((acc: Record<string, string>, item: any) => {
              const id = String(item?.id || '').trim();
              const name = String(item?.name || item?.code || '').trim();
              if (id && name) acc[id] = name;
              return acc;
            }, {})
          );
        } else {
          setCuisineNameMap({});
        }
      } finally {
        if (mounted) setIsLoadingDynamicData(false);
      }
    };

    void loadDynamicData();
    return () => {
      mounted = false;
    };
  }, [restaurant?.id]);

  const tabs = [
    { id: 'overview' as const, label: 'Overview', icon: Info },
    { id: 'order-online' as const, label: 'Order Online', icon: ShoppingBag },
    { id: 'offers' as const, label: 'Offers', icon: Tag },
    { id: 'reviews' as const, label: 'Reviews', icon: MessageSquare },
    { id: 'photos' as const, label: 'Photos', icon: Camera },
    { id: 'menu' as const, label: 'Menu', icon: FileText },
  ];

  return (
    <div>
      <div className="border-b border-gray-200 bg-white/95 backdrop-blur-sm">
        <div className="mx-auto max-w-screen-2xl px-3 sm:px-4">
          <div className="flex overflow-x-auto gap-1 no-scrollbar">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => onTabChange(tab.id)}
                  aria-current={isActive ? 'page' : undefined}
                  className={`flex items-center gap-1 whitespace-nowrap border-b-2 px-2.5 py-2 text-xs font-medium transition-colors sm:gap-2 sm:border-b-[3px] sm:px-6 sm:py-4 sm:text-base ${
                    isActive
                      ? 'border-rose-600 text-rose-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className={`h-3.5 w-3.5 sm:h-5 sm:w-5 ${isActive ? 'text-rose-600' : 'text-gray-500'}`} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-screen-2xl">
        {activeTab === 'overview' && (
          <OverviewTab
            restaurant={restaurant}
            details={details}
            cuisineNameMap={cuisineNameMap}
            loading={isLoadingDynamicData}
          />
        )}
        {activeTab === 'order-online' && children}
        {activeTab === 'offers' && (
          <OffersTab offers={offers || []} selectedOfferId={selectedOfferId || null} onOfferSelect={onOfferSelect} />
        )}
        {activeTab === 'reviews' && (
          <ReviewsTab restaurant={restaurant} reviews={reviews} loading={isLoadingDynamicData} />
        )}
        {activeTab === 'photos' && (
          <PhotosTab restaurant={restaurant} details={details} loading={isLoadingDynamicData} />
        )}
        {activeTab === 'menu' && (
          <MenuTab
            restaurant={restaurant}
            details={details}
            categories={menuCategories}
            items={menuItems}
            loading={isLoadingDynamicData}
          />
        )}
      </div>
    </div>
  );
}

function OffersTab({
  offers,
  selectedOfferId,
  onOfferSelect,
}: {
  offers: Coupon[];
  selectedOfferId: string | null;
  onOfferSelect?: (offer: Coupon) => void;
}) {
  const { formatMoney } = useLocale();

  const isActiveToday = (coupon: Coupon): boolean => {
    if (!coupon.isActive) return false;

    const now = new Date();
    const todayYmd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const toLocalYmd = (value: string): string | null => {
      const raw = String(value || '').trim();
      if (!raw) return null;
      const dt = new Date(raw);
      if (Number.isNaN(dt.getTime())) return null;
      return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    };

    const fromYmd = toLocalYmd(coupon.validFrom);
    const untilYmd = toLocalYmd(coupon.validUntil);

    if (fromYmd !== null && todayYmd < fromYmd) return false;
    if (untilYmd !== null && todayYmd > untilYmd) return false;
    return true;
  };

  const sorted = useMemo(() => {
    const safe = (Array.isArray(offers) ? offers : []).filter(isActiveToday);
    return [...safe].sort((a, b) => {
      const aValue = Number(a.discountValue || 0);
      const bValue = Number(b.discountValue || 0);
      if (a.discountType !== b.discountType) {
        if (a.discountType === 'percentage') return -1;
        if (b.discountType === 'percentage') return 1;
      }
      return bValue - aValue;
    });
  }, [offers]);

  const discountLabel = (coupon: Coupon): string => {
    const value = Number(coupon.discountValue || 0);
    if (!Number.isFinite(value) || value <= 0) return 'Special Offer';
    return coupon.discountType === 'percentage' ? `${Math.round(value)}% OFF` : `${formatMoney(value)} OFF`;
  };

  const conditionLabel = (coupon: Coupon): string => {
    const desc = String(coupon.description || '').trim();
    if (desc) return desc;
    const min = Number(coupon.minOrderValue || 0);
    if (Number.isFinite(min) && min > 0) return `on orders above ${formatMoney(min)}`;
    return 'Special Offer';
  };

  if (!sorted.length) {
    return (
      <div className="bg-white dark:bg-gradient-to-b dark:from-slate-950 dark:to-slate-900">
        <div className="px-3 py-5 sm:px-4 sm:py-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white sm:text-xl">Offers</h3>
          <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 dark:border-white/10 dark:bg-white/5 dark:text-white/80">
            No offers available right now.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gradient-to-b dark:from-slate-950 dark:to-slate-900">
      <div className="px-3 py-5 sm:px-4 sm:py-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white sm:text-xl">Offers</h3>
        <div className="mt-4 overflow-x-auto pb-2 no-scrollbar">
          <div className="flex w-max gap-3 pr-3">
            {sorted.slice(0, 20).map((coupon, index) => {
              const gradients = [
                'from-orange-500 to-amber-400',
                'from-emerald-600 to-green-500',
                'from-teal-600 to-emerald-500',
                'from-rose-500 to-red-500',
              ];
              const gradient = gradients[index % gradients.length];
              const isSelected = Boolean(selectedOfferId && String(coupon.id) === String(selectedOfferId));

              return (
                <button
                  key={coupon.id}
                  type="button"
                  onClick={() => onOfferSelect?.(coupon)}
                  className={`relative min-w-[220px] flex-shrink-0 overflow-hidden rounded-2xl bg-gradient-to-r ${gradient} p-3 shadow-lg sm:min-w-[240px]`}
                >
                  <div className="absolute -right-10 -top-10 h-24 w-24 rounded-full bg-white/15" />
                  <div className="absolute -bottom-12 -left-12 h-28 w-28 rounded-full bg-white/10" />
                  <div className="relative flex h-full flex-col justify-between gap-3">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-white/90">
                        {String(coupon.code || 'Offer').trim() || 'Offer'}
                      </div>
                      <div className="mt-2 inline-flex max-w-full items-center gap-2 rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-semibold text-white/95">
                        <span className="text-white/80">OFFER</span>
                        <span className="truncate font-extrabold tracking-wider">{String(coupon.code || '').trim()}</span>
                      </div>
                      <p className="mt-1 text-xl font-extrabold leading-tight text-white sm:text-2xl">
                        {discountLabel(coupon)}
                      </p>
                      <p className="mt-1 line-clamp-2 text-xs font-medium text-white/95 sm:text-sm">
                        {conditionLabel(coupon)}
                      </p>
                    </div>

                    {onOfferSelect ? (
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-semibold text-white/90">
                          {isSelected ? 'Applied to menu' : 'Tap to filter menu'}
                        </span>
                        {isSelected ? (
                          <span className="rounded-full bg-white/20 px-2 py-1 text-[10px] font-extrabold text-white">
                            SELECTED
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function OverviewTab({
  restaurant,
  details,
  cuisineNameMap,
  loading,
}: {
  restaurant: Restaurant;
  details: DynamicRestaurantData | null;
  cuisineNameMap: Record<string, string>;
  loading: boolean;
}) {
  const { formatMoney } = useLocale();
  const cuisineList = useMemo(() => {
    const fromDetails = Array.isArray(details?.cuisineDetails)
      ? details.cuisineDetails
          .map((item) => String(item?.name || item?.code || '').trim())
          .filter(Boolean)
      : [];
    if (fromDetails.length) return fromDetails;

    const rawValues = [
      ...parseStringList(details?.cuisine),
      ...((restaurant.cuisine || []).map((item) => String(item).trim()).filter(Boolean)),
    ];

    return Array.from(
      new Set(
        rawValues
          .map((item) => (isUuidLike(item) ? cuisineNameMap[item] || '' : item))
          .map((item) => item.trim())
          .filter(Boolean)
      )
    );
  }, [cuisineNameMap, details?.cuisine, details?.cuisineDetails, restaurant.cuisine]);

  const amenityList = useMemo(() => {
    const fromMaster = Array.isArray(details?.amenityDetails)
      ? details.amenityDetails
          .map((item) => String(item?.name || item?.code || '').trim())
          .filter(Boolean)
      : [];
    if (fromMaster.length) return fromMaster;
    return (restaurant.amenities || []).map((item) => String(item).trim()).filter(Boolean);
  }, [details?.amenityDetails, restaurant.amenities]);

  const rating = toNumber(details?.rating ?? restaurant.rating, restaurant.rating || 0);
  const totalReviews = toNumber(details?.totalReviews ?? restaurant.totalRatings, 0);
  const deliveryFee = toNumber(details?.deliveryFee ?? restaurant.deliveryFee, restaurant.deliveryFee || 0);
  const deliveryTime = String(details?.deliveryTime || restaurant.deliveryTime || 'N/A');
  const phone = String(details?.phone || restaurant.phone || '').trim();
  const email = String(details?.email || restaurant.email || '').trim();

  return (
    <div className="px-3 py-5 sm:px-4 sm:py-6">
      {loading && (
        <div className="mb-4 text-sm text-gray-500">Loading latest restaurant details...</div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">About {restaurant.name}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {details?.description || restaurant.description || 'Restaurant details not available.'}
            </p>
            {cuisineList.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {cuisineList.map((item) => (
                  <span
                    key={item}
                    className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium border border-green-200"
                  >
                    {item}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Opening Hours
            </h3>
            <div className="space-y-2">
              <div className="flex flex-col items-start justify-between gap-1 border-b border-gray-100 py-2 sm:flex-row sm:items-center">
                <span className="text-gray-700 font-medium">Daily</span>
                <span className="text-green-600 font-medium">
                  {formatTimeLabel(details?.openTime || restaurant.openTime)} -{' '}
                  {formatTimeLabel(details?.closeTime || restaurant.closeTime)}
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Features & Amenities</h3>
            {amenityList.length ? (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3">
                {amenityList.map((item) => (
                  <div key={item} className="flex items-center gap-2 text-gray-700">
                    <span className="text-green-600">+ {item}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No amenities configured yet.</p>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Info</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/15">
                  <Star className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Rating</p>
                  <p className="font-semibold text-gray-900">
                    {rating} ({totalReviews} reviews)
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/15">
                  <Clock className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Delivery Time</p>
                  <p className="font-semibold text-gray-900">{deliveryTime}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/15">
                  <IndianRupee className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Delivery Fee</p>
                  <p className="font-semibold text-green-600">
                    {deliveryFee === 0 ? 'FREE' : formatMoney(deliveryFee)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Us</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gray-500 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">Address</p>
                  <p className="text-gray-900">{buildAddressLabel(details || {}, restaurant)}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-gray-500 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="text-blue-600">{phone || 'Not available'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-gray-500 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="text-blue-600">{email || 'Not available'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReviewsTab({
  restaurant,
  reviews,
  loading,
}: {
  restaurant: Restaurant;
  reviews: DynamicReview[];
  loading: boolean;
}) {
  const avgRating =
    reviews.length > 0
      ? Number((reviews.reduce((sum, item) => sum + item.rating, 0) / reviews.length).toFixed(1))
      : 0;
  return (
    <div className="px-3 py-5 sm:px-4 sm:py-6">
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h3 className="mb-2 text-xl font-bold text-gray-900 sm:text-2xl">Customer Reviews</h3>
            <p className="text-gray-600">
              {reviews.length > 0
                ? `Based on ${reviews.length} customer review${reviews.length > 1 ? 's' : ''}`
                : 'No reviews available yet'}
            </p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-3">
              <div className="text-3xl font-bold text-gray-900 sm:text-4xl">{avgRating}</div>
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-5 h-5 ${
                    i < Math.floor(avgRating) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {loading && <div className="mb-4 text-sm text-gray-500">Loading latest reviews...</div>}

      <div>
        {!reviews.length ? (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500">
            No customer reviews yet for this restaurant.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {reviews.map((review) => (
              <div key={review.id} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="mb-3 flex items-start gap-3">
                  {review.avatar ? (
                    <img src={review.avatar} alt={review.name} className="h-12 w-12 rounded-full object-cover" />
                  ) : (
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-500/15 font-semibold text-blue-500">
                      {review.name.slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{review.name}</h4>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-4 h-4 ${
                              i < Math.floor(review.rating) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-sm text-gray-400">- {review.dateLabel}</span>
                    </div>
                  </div>
                </div>
                <p className="leading-relaxed text-gray-700">{review.comment}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PhotosTab({
  restaurant,
  details,
  loading,
}: {
  restaurant: Restaurant;
  details: DynamicRestaurantData | null;
  loading: boolean;
}) {
  const [selected, setSelected] = useState<{
    id: string;
    url: string;
    mediaType: GalleryMediaType;
    thumbnailUrl?: string | null;
    category: string;
  } | null>(null);

  const media = useMemo(() => {
    const fromMediaItems = Array.isArray(details?.mediaItems)
      ? details.mediaItems
          .map((item) => {
            const url = String(item?.mediaUrl || item?.media_url || '').trim();
            if (!url) return null;
            const rawType = String(item?.mediaType || '').trim().toUpperCase();
            const mediaType = (rawType === 'PHOTO' || rawType === 'VIDEO' ? rawType : inferMediaTypeFromUrl(url)) as GalleryMediaType;
            return {
              id: String(item?.id || url),
              url,
              mediaType,
              thumbnailUrl: (item?.thumbnailUrl ?? item?.thumbnail_url ?? null) as string | null,
              category: 'Gallery',
            };
          })
          .filter(Boolean)
      : [];

    if (fromMediaItems.length) return fromMediaItems as NonNullable<(typeof fromMediaItems)[number]>[];

    const fromLegacy = [
      ...(Array.isArray(details?.photos) ? details.photos.map((item) => ({ ...item, mediaType: 'PHOTO' as const })) : []),
      ...(Array.isArray(details?.videos) ? details.videos.map((item) => ({ ...item, mediaType: 'VIDEO' as const })) : []),
    ]
      .map((item: any) => {
        const url = String(item?.mediaUrl || item?.media_url || '').trim();
        if (!url) return null;
        const mediaType = (String(item?.mediaType || '').trim().toUpperCase() === 'VIDEO' ? 'VIDEO' : 'PHOTO') as GalleryMediaType;
        return {
          id: String(item?.id || url),
          url,
          mediaType,
          thumbnailUrl: (item?.thumbnailUrl ?? item?.thumbnail_url ?? null) as string | null,
          category: 'Gallery',
        };
      })
      .filter(Boolean) as Array<{
      id: string;
      url: string;
      mediaType: GalleryMediaType;
      thumbnailUrl?: string | null;
      category: string;
    }>;

    if (fromLegacy.length) return fromLegacy;

    const fallbacks = [restaurant.coverImage, restaurant.banner, restaurant.logo]
      .map((url, index) => ({
        id: `fallback-${index}`,
        url: String(url || '').trim(),
        mediaType: inferMediaTypeFromUrl(String(url || '').trim()),
        thumbnailUrl: null as string | null,
        category: 'Restaurant',
      }))
      .filter((item) => item.url);

    return fallbacks;
  }, [
    details?.mediaItems,
    details?.photos,
    details?.videos,
    restaurant.coverImage,
    restaurant.banner,
    restaurant.logo,
  ]);

  return (
    <div className="px-3 py-5 sm:px-4 sm:py-6">
      <div className="mb-6">
        <h3 className="mb-2 text-xl font-bold text-gray-900 sm:text-2xl">Gallery</h3>
        <p className="text-gray-600">Photos and videos from this restaurant profile and gallery</p>
      </div>

      {loading && <div className="mb-4 text-sm text-gray-500">Loading latest media...</div>}

      {!media.length ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-500">
          No media available.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3">
          {media.map((item) => (
            <button
              type="button"
              key={item.id}
              onClick={() => setSelected(item)}
              className="relative aspect-square rounded-xl overflow-hidden group cursor-pointer shadow-md hover:shadow-xl transition-shadow text-left"
              aria-label={item.mediaType === 'VIDEO' ? 'Open video' : 'Open photo'}
            >
              {item.mediaType === 'VIDEO' ? (
                <>
                  <video
                    src={item.url}
                    poster={item.thumbnailUrl || undefined}
                    muted
                    playsInline
                    preload="metadata"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <PlayCircle className="w-10 h-10 text-white opacity-80 group-hover:opacity-100 transition-opacity" />
                  </div>
                </>
              ) : (
                <img
                  src={item.url}
                  alt={`${restaurant.name} - ${item.category}`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
              )}
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setSelected(null)}
          role="dialog"
          aria-modal="true"
        >
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="absolute -top-10 right-0 inline-flex items-center justify-center rounded-full bg-white/90 p-2 text-gray-900 hover:bg-white"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="rounded-xl bg-black overflow-hidden shadow-2xl">
              {selected.mediaType === 'VIDEO' ? (
                <video
                  src={selected.url}
                  poster={selected.thumbnailUrl || undefined}
                  controls
                  autoPlay
                  playsInline
                  className="w-full max-h-[80vh] bg-black"
                />
              ) : (
                <img
                  src={selected.url}
                  alt={`${restaurant.name} - gallery`}
                  className="w-full max-h-[80vh] object-contain bg-black"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MenuTab({
  restaurant,
  details,
  categories,
  items,
  loading,
}: {
  restaurant: Restaurant;
  details: DynamicRestaurantData | null;
  categories: DynamicMenuCategory[];
  items: DynamicMenuItem[];
  loading: boolean;
}) {
  const { formatMoney } = useLocale();
  const grouped = useMemo(() => {
    const categoryMap = new Map<string, string>();
    categories.forEach((category) => categoryMap.set(category.id, category.name));

    const groups = new Map<string, { name: string; items: DynamicMenuItem[] }>();

    for (const item of items) {
      const groupId = item.categoryId || 'uncategorized';
      const groupName =
        item.categoryName || (groupId ? categoryMap.get(groupId) : undefined) || 'Menu';

      if (!groups.has(groupId)) {
        groups.set(groupId, { name: groupName, items: [] });
      }
      groups.get(groupId)?.items.push(item);
    }

    const ordered: Array<{ id: string; name: string; items: DynamicMenuItem[] }> = [];
    categories.forEach((category) => {
      const match = groups.get(category.id);
      if (match && match.items.length) {
        ordered.push({ id: category.id, name: category.name, items: match.items });
        groups.delete(category.id);
      }
    });

    groups.forEach((value, key) => {
      if (value.items.length) {
        ordered.push({ id: key, name: value.name, items: value.items });
      }
    });

    return ordered;
  }, [categories, items]);

  return (
    <div className="px-3 py-5 sm:px-4 sm:py-6">
      <div className="mb-6">
        <h3 className="mb-2 text-xl font-bold text-gray-900 sm:text-2xl">Full Menu</h3>
        <p className="text-gray-600">Menu items configured for {details?.name || restaurant.name}</p>
      </div>

      {loading && <div className="mb-4 text-sm text-gray-500">Loading latest menu...</div>}

      {!grouped.length ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-500">
          No menu items found for this restaurant.
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.map((category) => (
            <div key={category.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
              <h4 className="mb-4 inline-block border-b-2 border-blue-600 pb-2 text-lg font-semibold text-gray-900 sm:text-xl">
                {category.name}
              </h4>
              <div className="space-y-3 mt-4">
                {category.items.map((item) => (
                  <div
                    key={item.id}
                    className="flex flex-col items-start justify-between gap-2 rounded-lg px-3 py-2 transition-colors hover:bg-gray-50 sm:flex-row sm:items-center"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-4 h-4 border-2 rounded-sm flex items-center justify-center ${
                          item.isVeg ? 'border-green-600' : 'border-red-600'
                        }`}
                      >
                        <div className={`w-2 h-2 rounded-full ${item.isVeg ? 'bg-green-600' : 'bg-red-600'}`} />
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-gray-900 font-medium">{item.name}</span>
                        {formatServingSizeLabel(item.servingSize) && (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                            {formatServingSizeLabel(item.servingSize)}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-gray-900 font-semibold">{formatMoney(item.price)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
